import React from 'react';

import UserGuideViewer from '../UserGuideViewer';

import { PARROT_NUTRITIONAL_REQUIREMENTS } from '../../api/nutrition';

import type { NutritionReferenceView } from './nutritionManagementNav';



interface NutritionReferencePanelProps {

  view: NutritionReferenceView;

}



const mineralFields = [

  { key: 'calcium', label: 'Calcium', unit: 'g/kg' },

  { key: 'phosphorus', label: 'Phosphorus', unit: 'g/kg' },

  { key: 'magnesium', label: 'Magnesium', unit: 'g/kg' },

  { key: 'iron', label: 'Iron', unit: 'mg/kg' },

  { key: 'zinc', label: 'Zinc', unit: 'mg/kg' },

] as const;



const vitaminFields = [

  { key: 'vitaminA', label: 'Vitamin A', unit: 'IU/kg' },

  { key: 'vitaminD3', label: 'Vitamin D3', unit: 'IU/kg' },

  { key: 'vitaminE', label: 'Vitamin E', unit: 'IU/kg' },

  { key: 'thiamine', label: 'Thiamine', unit: 'mg/kg' },

  { key: 'riboflavin', label: 'Riboflavin', unit: 'mg/kg' },

] as const;



export default function NutritionReferencePanel({ view }: NutritionReferencePanelProps) {

  if (view === 'guide') {

    return <UserGuideViewer />;

  }



  const req = PARROT_NUTRITIONAL_REQUIREMENTS;



  return (

    <div className="space-y-6">

      <div className="rounded-lg border border-gray-200 bg-white p-6">

        <h3 className="text-lg font-semibold text-slate-900">Psittacine nutritional targets</h3>

        <p className="mt-1 text-sm text-slate-600">

          Reference ranges for formulating and evaluating daily feed mixes. Always confirm species-specific needs with an avian veterinarian.

        </p>

        <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">

          <div>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Macronutrients (g/kg)</h4>

            <dl className="mt-2 space-y-1 text-sm">

              <div className="flex justify-between"><dt>Protein</dt><dd>{req.protein.min}–{req.protein.max}</dd></div>

              <div className="flex justify-between"><dt>Fat</dt><dd>{req.fat.min}–{req.fat.max}</dd></div>

              <div className="flex justify-between"><dt>Fiber</dt><dd>{req.fiber.min}–{req.fiber.max}</dd></div>

              <div className="flex justify-between"><dt>Carbohydrate</dt><dd>{req.carbohydrate.min}–{req.carbohydrate.max}</dd></div>

            </dl>

          </div>

          <div>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key minerals</h4>

            <dl className="mt-2 space-y-1 text-sm">

              {mineralFields.map(({ key, label, unit }) => {

                const range = req[key];

                return (

                  <div key={key} className="flex justify-between">

                    <dt>{label}</dt>

                    <dd>{range.min}–{range.max} {unit}</dd>

                  </div>

                );

              })}

            </dl>

          </div>

          <div>

            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Key vitamins</h4>

            <dl className="mt-2 space-y-1 text-sm">

              {vitaminFields.map(({ key, label, unit }) => {

                const range = req[key];

                return (

                  <div key={key} className="flex justify-between">

                    <dt>{label}</dt>

                    <dd>{range.min}–{range.max} {unit}</dd>

                  </div>

                );

              })}

            </dl>

          </div>

        </div>

      </div>

      <div className="rounded-lg border border-dashed border-cyan-200 bg-cyan-50/40 p-4 text-sm text-slate-700">

        Use the <strong>Optimizer</strong> under Plans to balance mixes toward these targets, and review daily mix quality scores under Records.

      </div>

    </div>

  );

}

